import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useMutation } from "@apollo/client";
import { EDIT_ZONE_MEMBER_MUTATION } from "../graphql/mutations";
import { Snackbar, Alert } from "@mui/material";

const EditUserModal = ({ isOpen, onClose, user, onSuccess }) => {
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [editZoneMember] = useMutation(EDIT_ZONE_MEMBER_MUTATION);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const storedUserType = localStorage.getItem("user_type");

  useEffect(() => {
    if (isOpen && user) {
      setFormData({
        NAME: user.NAME || "",
        MOBILE1: user.MOBILE1 || "",
        MOBILE2: user.MOBILE2 || "",
        ADDRESS: user.ADDRESS || "",
        NAGAR: user.NAGAR || "",
        AREA: user.AREA || "",
        CITY: user.CITY || "",
        PINCODE: user.PINCODE || "",
        role: user.role || "user",
      });
      setFormErrors({});
    }
  }, [isOpen, user]);

  const handleOnChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const errors = {};
    // ✅ Added AREA as required
    const requiredFields = [
      "NAME",
      "MOBILE1",
      "ADDRESS",
      "AREA",
      "CITY",
      "PINCODE",
    ];
    requiredFields.forEach((field) => {
      if (!formData[field] || formData[field].trim() === "") {
        errors[field] = `${field} is required`;
      }
    });
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    try {
      const { data } = await editZoneMember({
        variables: {
          mobile: user?.MOBILE1,
          ...formData,
        },
      });

      if (data?.editZoneMember) {
        setSnackbarMessage("Member updated successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        onClose();
        onSuccess?.();
      }
    } catch (error) {
      setSnackbarMessage(
        error?.graphQLErrors?.[0]?.message || "Failed to update member."
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const fields = [
    "NAME",
    "MOBILE1",
    "MOBILE2",
    "ADDRESS",
    "NAGAR",
    "AREA",
    "CITY",
    "PINCODE",
  ];

  const visibleFields =
    storedUserType === "superuser" ? [...fields, "role"] : fields;

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.3 }}
            className="fixed top-0 right-0 w-full max-w-xl pt-24 h-full shadow-xl z-50 overflow-y-auto bg-[#F8F9E9]"
          >
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-xl font-semibold">Edit Member Profile</h2>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-800"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-5 bg-[#F8F9E9]">
              {visibleFields.map((field) => (
                <div key={field} className="flex flex-col space-y-1">
                  <label className="text-sm font-medium text-gray-700 text-left">
                    {/* ✅ UI change: role → ROLE */}
                    {field === "role" ? "ROLE" : field}
                    {[
                      "NAME",
                      "MOBILE1",
                      "ADDRESS",
                      "AREA",
                      "CITY",
                      "PINCODE",
                    ].includes(field) && (
                      <span className="text-red-500 ml-1">*</span>
                    )}
                  </label>

                  {field === "role" ? (
                    <select
                      name="role"
                      value={formData.role || "user"}
                      onChange={handleOnChange}
                      className={`mt-1 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 ${
                        formErrors[field] ? "border-red-500" : ""
                      }`}
                    >
                      <option value="user">User</option>
                      <option value="superuser">Superuser</option>
                    </select>
                  ) : (
                    <input
                      type="text"
                      name={field}
                      value={formData[field] || ""}
                      onChange={handleOnChange}
                      className={`mt-1 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 ${
                        formErrors[field] ? "border-red-500" : ""
                      }`}
                    />
                  )}

                  {formErrors[field] && (
                    <p className="text-sm text-red-500 text-left pl-1">
                      {formErrors[field]}
                    </p>
                  )}
                </div>
              ))}
            </div>

            <div className="p-4 border-t flex justify-end gap-4 bg-[#F8F9E9]">
              <button
                onClick={onClose}
                style={{ backgroundColor: "#E52029", borderRadius: "999px" }}
                className="px-4 py-2 text-white hover:opacity-90"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                style={{ backgroundColor: "green", borderRadius: "999px" }}
                className="px-4 py-2 text-white hover:opacity-90"
              >
                Save Changes
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snackbarSeverity} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EditUserModal;
